import { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { BrandIcoMock } from "./BrandIcoMock";
import { HomeMock } from "./HomeMock";
import { FindARunMock } from "./FindARunMock";
import { PostMatchMock } from "./PostMatchMock";
import { RankingsMock } from "./RankingsMock";
import { ReelsMock } from "./ReelsMock";
import { BookACourtMock } from "./BookACourtMock";
import { VenueInAppMock } from "./VenueInAppMock";

export interface Screen {
  label: string;
  caption: string;
  Component: React.ComponentType<{ className?: string }>;
}

/** Captions framed for venue partners ("your venue shows up here"). */
export const VENUE_AUDIENCE_SCREENS: Screen[] = [
  {
    label: "Whistll",
    caption: "The app players open first",
    Component: BrandIcoMock,
  },
  {
    label: "Home",
    caption: "Your venue surfaces in Suggested Courts",
    Component: HomeMock,
  },
  {
    label: "Find a Run",
    caption: "Players see open runs at your courts",
    Component: FindARunMock,
  },
  {
    label: "Post Match",
    caption: "Every game ends with a live court rating",
    Component: PostMatchMock,
  },
  {
    label: "Rankings",
    caption: "Your venue's teams ranked across the city",
    Component: RankingsMock,
  },
  {
    label: "Reels",
    caption: "Clips from your courts surface to the whole city",
    Component: ReelsMock,
  },
  {
    label: "Book a Court",
    caption: "Your venue in the court browser",
    Component: BookACourtMock,
  },
  {
    label: "Venue detail",
    caption: "Full listing with photos and pitches",
    Component: VenueInAppMock,
  },
];

/** Captions framed for players ("here's what you do in the app"). */
export const PLAYER_AUDIENCE_SCREENS: Screen[] = [
  {
    label: "Whistll",
    caption: "Built for real hoopers",
    Component: BrandIcoMock,
  },
  {
    label: "Home",
    caption: "Book, find a run, or compete in one tap",
    Component: HomeMock,
  },
  {
    label: "Find a Run",
    caption: "Jump into open games near you",
    Component: FindARunMock,
  },
  {
    label: "Post Match",
    caption: "Rate the court, vote POTM, settle the result",
    Component: PostMatchMock,
  },
  {
    label: "Rankings",
    caption: "Climb the London leaderboard. Earn badges.",
    Component: RankingsMock,
  },
  {
    label: "Reels",
    caption: "Swipe through the best clips from across the city",
    Component: ReelsMock,
  },
  {
    label: "Book a Court",
    caption: "Live availability and transparent pricing",
    Component: BookACourtMock,
  },
  {
    label: "Venue detail",
    caption: "Every court verified, every price upfront",
    Component: VenueInAppMock,
  },
];

/**
 * Horizontal snap-scroll gallery of phone mocks.
 * On touch devices users swipe; on desktop they scroll or click the label pills.
 * Pass `screens` to choose which audience framing to show (defaults to venue).
 */
export function AppMockGallery({
  screens = VENUE_AUDIENCE_SCREENS,
}: {
  screens?: Screen[];
}) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const onScroll = () => {
      const children = Array.from(scroller.children) as HTMLElement[];
      if (children.length === 0) return;
      const scrollerRect = scroller.getBoundingClientRect();
      const scrollerCentre = scrollerRect.left + scrollerRect.width / 2;
      let bestIdx = 0;
      let bestDist = Infinity;
      children.forEach((child, i) => {
        const rect = child.getBoundingClientRect();
        const centre = rect.left + rect.width / 2;
        const dist = Math.abs(centre - scrollerCentre);
        if (dist < bestDist) {
          bestDist = dist;
          bestIdx = i;
        }
      });
      setActive(bestIdx);
    };

    // Initial look: first mock flush to the left (with a small gutter) rather
    // than centred. Padding is symmetric so clicking any pill (including the
    // first) can still smooth-scroll its mock to centre; we just shift the
    // initial scroll position so the gallery doesn't load with a huge left gap.
    const first = scroller.children[0] as HTMLElement | undefined;
    if (first) {
      const initialGutter =
        typeof window !== "undefined" && window.innerWidth >= 640 ? 40 : 24;
      scroller.scrollLeft = Math.max(0, first.offsetLeft - initialGutter);
    }

    onScroll();
    scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => scroller.removeEventListener("scroll", onScroll);
  }, []);

  // Cancel handle for any in-flight arrow-scroll animation. Lets a quick
  // second click pre-empt the first instead of stacking two animations
  // on the same scroller.
  const scrollAnimRef = useRef<number | null>(null);

  const scrollTo = (idx: number) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const child = scroller.children[idx] as HTMLElement | undefined;
    if (!child) return;
    const target =
      child.offsetLeft - (scroller.clientWidth - child.offsetWidth) / 2;

    // Why a custom animation instead of `scroller.scrollTo({ behavior: "smooth" })`?
    // The native smooth-scroll uses an ease-in-out curve, which accelerates at
    // the start and decelerates at the end - that's why arrow clicks felt
    // "draggy" compared to finger swipes. A finger flick is pure deceleration
    // (you push hard, the gallery coasts and settles). Cubic ease-out replicates
    // that: full speed at t=0, smooth glide into the target at t=1.
    if (scrollAnimRef.current !== null) {
      cancelAnimationFrame(scrollAnimRef.current);
      scrollAnimRef.current = null;
    }
    const start = scroller.scrollLeft;
    const distance = target - start;
    if (Math.abs(distance) < 1) return;
    // 320ms is the sweet spot - fast enough to feel responsive, long enough
    // for the ease-out curve to read as a smooth flick rather than a jump.
    const duration = 320;
    const startTime = performance.now();

    const step = (now: number) => {
      const t = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      scroller.scrollLeft = start + distance * eased;
      if (t < 1) {
        scrollAnimRef.current = requestAnimationFrame(step);
      } else {
        scrollAnimRef.current = null;
      }
    };
    scrollAnimRef.current = requestAnimationFrame(step);
  };

  // Arrow nav - clamp at boundaries. Hidden when there's no direction
  // to scroll so the chevrons don't sit dead.
  const goPrev = () => scrollTo(Math.max(0, active - 1));
  const goNext = () => scrollTo(Math.min(screens.length - 1, active + 1));
  const canPrev = active > 0;
  const canNext = active < screens.length - 1;

  return (
    <div className="relative">
      {/* Edge fades for polish on desktop - only in dark mode. In light
          mode the dark phone frames visible through the fade read as an
          ugly smudge, so we skip it. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 z-10 hidden w-16 bg-gradient-to-r from-background to-transparent dark:md:block"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 z-10 hidden w-16 bg-gradient-to-l from-background to-transparent dark:md:block"
      />

      {/* Prev / Next arrow buttons.
          Visible at all viewports: on desktop they're the primary nav,
          on mobile they back up swipe for one-handed thumb reach.
          - Fixed size (h-11 w-11) so they read as a real tap target.
          - Solid card background + border so they don't dissolve into
            the page; they're meant to *look* like buttons.
          - Hover swaps to volt for an obvious affordance cue.
          - At boundaries we hide them entirely rather than ghost
            them - a half-faded disabled button looks broken. */}
      <button
        type="button"
        onClick={goPrev}
        disabled={!canPrev}
        aria-label="Previous screen"
        className={
          "absolute left-1 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-lg transition-all sm:left-2 " +
          (canPrev
            ? "hover:scale-110 hover:border-volt hover:bg-volt hover:text-primary-foreground"
            : "pointer-events-none opacity-0")
        }
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        type="button"
        onClick={goNext}
        disabled={!canNext}
        aria-label="Next screen"
        className={
          "absolute right-1 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-lg transition-all sm:right-2 " +
          (canNext
            ? "hover:scale-110 hover:border-volt hover:bg-volt hover:text-primary-foreground"
            : "pointer-events-none opacity-0")
        }
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      {/* Scroller */}
      <div
        ref={scrollerRef}
        className="flex snap-x snap-mandatory gap-8 overflow-x-auto scroll-smooth px-[calc(50%-140px)] py-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        role="region"
        aria-label="App screen previews"
      >
        {screens.map(({ label, caption, Component }, i) => (
          <div
            key={label}
            className="flex shrink-0 snap-center flex-col items-center gap-3"
          >
            <Component />
            <div className="text-center">
              <p className="text-sm font-semibold">{label}</p>
              <p className="mt-0.5 max-w-[240px] text-xs text-muted-foreground">
                {caption}
              </p>
            </div>
            <span className="sr-only">{`Screen ${i + 1} of ${screens.length}`}</span>
          </div>
        ))}
      </div>

      {/* Dot indicators + labels */}
      <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
        {screens.map((screen, i) => (
          <button
            key={screen.label}
            type="button"
            onClick={() => scrollTo(i)}
            className={
              "rounded-full px-3 py-1 text-xs font-medium transition-colors " +
              (active === i
                ? "bg-volt text-primary-foreground"
                : "bg-secondary text-muted-foreground hover:bg-volt-subtle hover:text-foreground")
            }
            aria-label={`Jump to ${screen.label}`}
            aria-current={active === i ? "true" : undefined}
          >
            {screen.label}
          </button>
        ))}
      </div>
    </div>
  );
}
